import { useState, useEffect, useRef } from "react";
import { Minus, Plus } from "lucide-react";

export interface QuantityControlProps {
  value: number;
  onChange: (value: number) => void;
  min?: number;
  max?: number;
  step?: number;
  allowDecimal?: boolean;
  disabled?: boolean;
  size?: "sm" | "md" | "lg";
  unitName?: string;
  className?: string;
  ariaLabel?: string;
}

export function QuantityControl({
  value,
  onChange,
  min = 0,
  max = 999999,
  step = 1,
  allowDecimal = true,
  disabled = false,
  size = "md",
  unitName,
  className = "",
  ariaLabel = "Số lượng",
}: QuantityControlProps) {
  const [text, setText] = useState(String(value));
  const inputRef = useRef<HTMLInputElement>(null);

  useEffect(() => {
    setText(String(value));
  }, [value]);

  function commit(raw: string) {
    const cleaned = raw.replace(/,/g, ".").trim();
    if (cleaned === "" || isNaN(Number(cleaned))) {
      setText(String(value));
      return;
    }
    let parsed = Number(cleaned);
    if (!allowDecimal) {
      parsed = Math.round(parsed);
    } else {
      parsed = Math.round((parsed + Number.EPSILON) * 1000) / 1000;
    }
    if (min !== undefined && parsed < min) parsed = min;
    if (max !== undefined && parsed > max) parsed = max;
    setText(String(parsed));
    onChange(parsed);
  }

  function handleBlur() {
    commit(text);
  }

  function handleKeyDown(event: React.KeyboardEvent<HTMLInputElement>) {
    if (event.key === "Enter") {
      commit(text);
      inputRef.current?.blur();
    } else if (event.key === "ArrowUp") {
      event.preventDefault();
      increment();
    } else if (event.key === "ArrowDown") {
      event.preventDefault();
      decrement();
    }
  }

  function decrement() {
    if (disabled) return;
    const next = allowDecimal
      ? Math.round((value - step + Number.EPSILON) * 1000) / 1000
      : Math.round(value - step);
    const clamped = min !== undefined ? Math.max(min, next) : next;
    setText(String(clamped));
    onChange(clamped);
  }

  function increment() {
    if (disabled) return;
    const next = allowDecimal
      ? Math.round((value + step + Number.EPSILON) * 1000) / 1000
      : Math.round(value + step);
    const clamped = max !== undefined ? Math.min(max, next) : next;
    setText(String(clamped));
    onChange(clamped);
  }

  return (
    <div
      className={`quantity-control-group quantity-control-${size} ${className}`}
      style={{
        display: "inline-flex",
        alignItems: "center",
        borderRadius: "var(--radius-sm, 6px)",
        border: "1px solid var(--color-border, #d1d5db)",
        backgroundColor: "var(--color-surface, #ffffff)",
        overflow: "hidden",
      }}
    >
      <button
        type="button"
        disabled={disabled || (min !== undefined && value <= min)}
        onClick={decrement}
        aria-label="Giảm số lượng"
        style={{
          border: "none",
          background: "transparent",
          minWidth: size === "sm" ? 32 : 40,
          height: size === "sm" ? 32 : 40,
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
          cursor: disabled || (min !== undefined && value <= min) ? "not-allowed" : "pointer",
          color: disabled || (min !== undefined && value <= min) ? "var(--color-muted, #9ca3af)" : "var(--color-text, #111827)",
          padding: 0,
        }}
      >
        <Minus size={size === "sm" ? 14 : 18} />
      </button>

      <input
        ref={inputRef}
        type="text"
        inputMode={allowDecimal ? "decimal" : "numeric"}
        disabled={disabled}
        aria-label={ariaLabel}
        value={text}
        onChange={(e) => setText(e.target.value)}
        onFocus={(e) => e.target.select()}
        onBlur={handleBlur}
        onKeyDown={handleKeyDown}
        style={{
          border: "none",
          borderLeft: "1px solid var(--color-border, #e5e7eb)",
          borderRight: "1px solid var(--color-border, #e5e7eb)",
          textAlign: "center",
          width: size === "sm" ? 52 : 68,
          height: size === "sm" ? 32 : 40,
          fontSize: size === "sm" ? "0.9rem" : "1rem",
          fontWeight: 600,
          padding: "0 4px",
          outline: "none",
          backgroundColor: "transparent",
          color: "inherit",
        }}
      />

      <button
        type="button"
        disabled={disabled || (max !== undefined && value >= max)}
        onClick={increment}
        aria-label="Tăng số lượng"
        style={{
          border: "none",
          background: "transparent",
          minWidth: size === "sm" ? 32 : 40,
          height: size === "sm" ? 32 : 40,
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
          cursor: disabled || (max !== undefined && value >= max) ? "not-allowed" : "pointer",
          color: disabled || (max !== undefined && value >= max) ? "var(--color-muted, #9ca3af)" : "var(--color-text, #111827)",
          padding: 0,
        }}
      >
        <Plus size={size === "sm" ? 14 : 18} />
      </button>

      {unitName ? (
        <span
          style={{
            padding: "0 8px",
            fontSize: "0.85rem",
            color: "var(--color-muted, #6b7280)",
            background: "var(--color-subtle, #f3f4f6)",
            height: "100%",
            display: "flex",
            alignItems: "center",
            borderLeft: "1px solid var(--color-border, #e5e7eb)",
          }}
        >
          {unitName}
        </span>
      ) : null}
    </div>
  );
}
